import { EDecision, EPaymentFrequency, EPaymentMethod, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate, formatDateTime } from '@if/core/utils'
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Divider, IconButton, Link, Paper, Stack, Typography } from '@mui/material'
import { getProvidersInsurancePlans } from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useAppSelector } from '@src/data/store'
import { appSelectors } from '@src/data/store/AppStore'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import productsWorksheetEffects from '@src/data/store/ProductsWorksheet/products-worksheet-effects'
import { Constants, CreditApplication, ETermMax } from '@src/data/types'
import { ProductsProvider } from '@src/data/types/ProductsProvider'
import { ProductsInsurancePlan, ProductsWorksheet, ProductsWorksheetDto } from '@src/data/types/ProductsWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { getMinMaxPaymentDates } from '@src/services/paymentDate-utils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ProductInsurance from './ProductInsurance'

interface Props {
  worksheet: ProductsWorksheet | null
  creditApplication: CreditApplication
  defaultPaymentMethod: EPaymentMethod
  onWorksheetHistoryClicked: () => void
  editDisabled: boolean
}

const ProductsWorksheetInformationSection = ({
  worksheet,
  creditApplication,
  defaultPaymentMethod,
  onWorksheetHistoryClicked,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const merchantPaymentPlan = useAppSelector(creditSelectors.getMerchantPaymentPlan)
  const paymentFrequency = EPaymentFrequency.Monthly
  const deliveryDate = formatDate(new Date())
  const [minFirstPaymentOn] = getMinMaxPaymentDates(deliveryDate, paymentFrequency)
  const editWorksheetDisabled = creditApplication.finalCreditDecision.decision !== EDecision.Approved || editDisabled
  const featureFlags = useSelector(appSelectors.getFeatureFlags)
  const showAdditionalSummaryVariables = featureFlags.showAdditionalSummaryVariables
  const [productsInsurances, setProductsInsurances] = useState<ProductsInsurancePlan[]>([])
  const productsProviders: ProductsProvider[] = useAppSelector(appSelectors.getProductProviders)

  useEffect(() => {
    if (worksheet) setProductsInsurances(getProvidersInsurancePlans(worksheet, productsProviders))
  }, [productsProviders, worksheet])

  // event handlers
  const createWorksheet = () => {
    const route = `/Applications/products/${creditApplication.id}/worksheet/edit`
    if (worksheet === null) {
      const term = merchantPaymentPlan ? merchantPaymentPlan.loanTerm : ETermMax.twelve
      const newWorksheet: ProductsWorksheetDto = {
        creditApplicationId: creditApplication.id,
        amountRequested: Constants.MinimumProductsLoanAmount,
        deliveryOn: deliveryDate,
        firstPaymentOn: formatDate(minFirstPaymentOn),
        paymentFrequency,
        term,
        includeInsurance: false,
        addFastPayments: false,
        fundConfirmationNote: null,
        paymentPlanId: merchantPaymentPlan ? merchantPaymentPlan.id : EPaymentPlan.RegularDailyInterests,
        merchantPayments: [
          { merchantId: creditApplication.merchantId, amount: 750, paymentMethod: defaultPaymentMethod },
        ],
        status: EWorksheetStatus.Draft,
        vehicleFinancingTerm: merchantPaymentPlan ? merchantPaymentPlan.loanTerm : ETermMax.twelve,
        creditInsurance: null,
        extendedWarranty: null,
        replacementOrGapInsurance: null,
      }

      dispatchEffect(productsWorksheetEffects.create(newWorksheet))
        .then(() => navigate(route))
        .catch(reportErrorToConsole)
    } else {
      navigate(route)
    }
  }
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2} marginBottom={0.5}>
            <ButtonGroup variant="outlined" size="large">
              <Button
                id="btnEditWorksheet"
                component={Link}
                variant="outlined"
                href={`/Applications/products/${creditApplication.id}/worksheet`}
                disabled={!worksheet}
              >
                {t('worksheet.financing')}
              </Button>
              <Button size="small" onClick={createWorksheet} disabled={editWorksheetDisabled}>
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            alignContent="center"
            divider={<Divider orientation="vertical" flexItem sx={{ height: 25, alignSelf: 'center' }} />}
          >
            <Typography variant="body1" component="div" gutterBottom>
              {t('common.LastModification')} : {worksheet && formatDateTime(worksheet?.updatedOn)}
            </Typography>

            <IconButton
              onClick={onWorksheetHistoryClicked}
              disabled={worksheet === null}
              sx={[
                {
                  fontSize: 'large',
                },
                worksheet === null
                  ? {
                      color: '',
                    }
                  : {
                      color: 'primary.main',
                    },
              ]}
            >
              <HistoryIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.decision')} :{' '}
              {worksheet?.finalDecision.decision &&
                t(`EWorksheetDecision.${worksheet.finalDecision.decision}`, { ns: 'enum' })}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
            {showAdditionalSummaryVariables && (
              <>
                <Typography variant="body1" component="div" gutterBottom>
                  {t('worksheet.firstPaymentOn')} : {formatDate(worksheet?.firstPaymentOn)}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                  {t('worksheet.lastPaymentDate')} : {formatDate(worksheet?.lastPaymentOn)}
                </Typography>
              </>
            )}
          </span>
          {showAdditionalSummaryVariables &&
            productsInsurances.map((insurance, index) => (
              <span key={`${insurance.type}-${index}`}>
                <ProductInsurance insurance={insurance} />
              </span>
            ))}
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheetCommon.amountRequested')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default ProductsWorksheetInformationSection
